import { stringTemplate } from '@oy/uidesktop';
import {
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
} from 'react-hook-form';
import { getErrorTranslationTemplate } from 'src/utilities/Yup';
import useTranslate from './useTranslate';

type FormHelperParams = {
  translateLabel?: boolean;
};

export const useFormHelper = <T extends FieldValues>({
  translateLabel = true,
}: FormHelperParams = {}) => {
  const { getTranslation } = useTranslate();

  const getErrorMessage = (errorMessage?: string) => {
    if (!errorMessage) return '';
    const { key, template } = getErrorTranslationTemplate(errorMessage);
    let label = template.path ?? '';
    if (label && translateLabel) {
      label = getTranslation(label);
    }
    return stringTemplate({
      str: getTranslation(key),
      template: {
        ...template,
        path: label,
      },
    });
  };

  const bindInput = (
    field: ControllerRenderProps<T>,
    fieldState: ControllerFieldState,
  ) => {
    const { value, onBlur, onChange, name } = field;
    const { error } = fieldState;

    return {
      name,
      value,
      errorMessage: getErrorMessage(error?.message),
      onBlur,
      onChange,
    };
  };

  const bindInputV2 = <U extends FieldValues, V extends FieldPath<U>>(
    field: ControllerRenderProps<U, V>,
    fieldState: ControllerFieldState,
  ) => {
    const { name, value, onBlur, onChange } = field;
    const { error } = fieldState;

    let errorMessage = error?.message;
    if (Array.isArray(error)) {
      const firstArrayError = error?.find(e => !!e);
      errorMessage = firstArrayError?.message;
    }

    return {
      name,
      value,
      errorMessage: getErrorMessage(errorMessage),
      onBlur,
      onChange,
    };
  };

  return { bindInput, bindInputV2, getErrorMessage };
};
